import { format } from 'date-fns'
import { useMemo } from 'react'
import { ComparisonLineChart, LINE_CHART_EMPTY_DATA } from 'src/components/analytics/charts/ComparisonLineChart'
import { NoData } from 'src/components/analytics/common/NoData'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import { ComparisonLineChartDataType } from 'src/components/analytics/types/comparison-line-chart'
import { useChartData } from 'src/components/overview/useChartData'
import { formatDateRange } from 'src/components/sso-analytics/charts/TotalIdentificationRate/parser'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId, WidgetSections } from 'src/lib/contexts/AnalyticsContext/types'
import { ChartSkeleton } from '../common/Skeleton'
import { getTimeIncrement, sortDataByDate } from '../common/utils'
import { formatNumberToTwoDecimals } from 'src/lib/utils/analytics'
import { useWidgetConfig } from '../common/useWidgetConfig'

export default function ComparisonRate() {
  const {
    getters: { selectedTimeScale, refreshKey, dateFilter },
    actions: { setAlertDialogState }
  } = useAnalytics()

  const { threshold_alerts_supported } = useWidgetConfig(WidgetSections.conversion, WidgetId.conversion_rate)

  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.conversion_rate,
      dependencies: {
        endpoints: ['/conversions', '/conversion-rate']
      }
    },
    refreshKey
  })

  const conversionRateData: ComparisonLineChartDataType & {
    totalConversionRatePercentage: number
    conversionRatePercentageChange: number
  } = useMemo(() => {
    const rawData = data[0]?.data?.result?.widgetData?.data?.[0]?.data
    const rawComparedData = data[0]?.data?.result?.widgetData?.compared_data?.[0]?.data

    const conversionRateData = formatNumberToTwoDecimals(data[1]?.data?.result?.widgetData?.data || 0)
    const conversionRateComparedData = formatNumberToTwoDecimals(
      data[1]?.data?.result?.widgetData?.compared_data?.toFixed(2) || 0
    )

    if (rawData && rawData.every((item: any) => item.checkout_initiated === 0)) {
      if (rawComparedData && rawComparedData.every((item: any) => item.checkout_initiated === 0)) {
        return LINE_CHART_EMPTY_DATA
      }
      return LINE_CHART_EMPTY_DATA
    }

    if (!rawData || rawData.length === 0) {
      return LINE_CHART_EMPTY_DATA
    }

    const currentData = rawData.sort(sortDataByDate)
    let previousData: any[] = []
    if (rawComparedData) {
      previousData = rawComparedData.sort(sortDataByDate)
    }

    const currentRange =
      new Date(currentData[currentData.length - 1].date).getTime() - new Date(currentData[0].date).getTime()
    let previousRange = 0
    if (previousData.length > 0) {
      previousRange =
        new Date(previousData[previousData.length - 1].date).getTime() - new Date(previousData[0].date).getTime()
    }

    const isCurrentPrimary = currentRange >= previousRange
    const primaryDataset = isCurrentPrimary ? currentData : previousData

    const calculateTotalConversionRatePercentage = (data: any[]) => {
      const totalUsers = data.reduce((acc, curr) => acc + curr.checkout_initiated, 0)
      if (totalUsers === 0) return 0
      return (data.reduce((acc, curr) => acc + curr.order_completed, 0) / totalUsers) * 100
    }

    const calculateRate = (item: any): number => {
      if (item.checkout_initiated === 0 || item.order_completed === 0) return 0
      return parseFloat(((item.order_completed / item.checkout_initiated) * 100).toFixed(2))
    }

    const dataIterator = (data: any[]) => {
      return data.map((item, index) => ({
        date: new Date(item.date),
        displayDate: new Date(new Date(primaryDataset[0].date).getTime() + index * getTimeIncrement(selectedTimeScale)),
        rate: calculateRate(item),
        actualDate: new Date(item.date)
      }))
    }

    const mappedCurrentData = dataIterator(currentData)
    const mappedPreviousData = dataIterator(previousData)

    return {
      currentWeekData: mappedCurrentData,
      previousWeekData: mappedPreviousData,
      primaryDataset: isCurrentPrimary ? mappedCurrentData : mappedPreviousData,
      secondaryDataset: isCurrentPrimary ? mappedPreviousData : mappedCurrentData,
      isCurrentPrimary,
      currentDateRange: {
        from: new Date(currentData[0].date),
        to: new Date(currentData[currentData.length - 1].date)
      },
      previousDateRange:
        previousData.length > 0
          ? {
              from: new Date(previousData[0].date),
              to: new Date(previousData[previousData.length - 1].date)
            }
          : null,
      totalConversionRatePercentage: conversionRateData,
      conversionRatePercentageChange:
        dateFilter.comparison !== 'none'
          ? formatNumberToTwoDecimals(conversionRateData - conversionRateComparedData)
          : 0
    }
  }, [data, selectedTimeScale])

  const currentDateRange = conversionRateData.currentDateRange
  const previousDateRange = conversionRateData.previousDateRange

  const currentDateRangeLegend =
    conversionRateData.currentWeekData.length === 1
      ? format(currentDateRange.from, 'MMM dd, yyyy')
      : formatDateRange(currentDateRange.from, currentDateRange.to, selectedTimeScale)

  const previousDateRangeLegend = previousDateRange
    ? formatDateRange(previousDateRange.from, previousDateRange.to, selectedTimeScale)
    : null

  const totalConversionRatePercentage = conversionRateData.totalConversionRatePercentage
  const conversionRatePercentageChange = conversionRateData.conversionRatePercentageChange

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (conversionRateData.currentWeekData.length > 0) {
      return (
        <>
          <div className='flex items-center gap-2 my-2'>
            <div className='text-[18px] font-bold text-carbon mb-1'>{totalConversionRatePercentage.toFixed(2)}%</div>
            <TrendIndicator value={conversionRatePercentageChange} formatter={value => `${Math.abs(value)}%`} />
          </div>
          <div className='w-full h-[84%]'>
            <ComparisonLineChart
              thresholdAlertConfig={{
                isAlertsSupported: Boolean(threshold_alerts_supported),
                onClickAlert: (rate: number) => setAlertDialogState(true, WidgetId.conversion_rate, rate)
              }}
              data={conversionRateData}
              isNoComparison={false}
              selectedTimeScale={selectedTimeScale}
              tooltipTitle='Conversion Rate'
            />
          </div>
          <div className='rounded-lg flex-1'>
            <div className='flex gap-6 mb-4 px-4 text-[#949494] text-xs'>
              <div className='flex items-center gap-2'>
                <div className='w-8 h-1 bg-primary-dark rounded-full'></div>
                <span>{currentDateRangeLegend}</span>
              </div>
              {previousDateRangeLegend && (
                <div className='flex items-center gap-2'>
                  <div className='w-8 h-1 border-t-2 border-dashed border-[#949494]'></div>
                  <span>{previousDateRangeLegend}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}
